import type { InstallableModel, ModelDtype, VoiceModel, VoiceModelWeights } from '../types/LocalAiTypes';

/** Whether a weight format carries half-precision values, and so requires a GPU that has them. */
export function needsGpu(dtype: ModelDtype | ''): boolean {
  return dtype === 'fp16' || dtype.endsWith('f16');
}

/**
 * Narrows a published model to the one weight format this device can run.
 *
 * Preference is by size among the formats that will actually run, which is the right ordering
 * for a download the operator waits on and — not by coincidence — also the faster one: `q4f16`
 * is smaller than `q4` because half the numbers in it are half as wide, and a GPU that can read
 * them runs them quicker for the same reason. A device without those shaders is not offered a
 * slower version of that choice, it is offered the format that works.
 *
 * @param halfPrecision Whether the GPU compiles fp16 shaders — see `supportsHalfPrecision`.
 */
export function resolveModel(model: VoiceModel, halfPrecision: boolean): InstallableModel {
  const shared = model.assets.reduce((total, asset) => total + asset.bytes, 0);
  const base = {
    key: model.key,
    id: model.id,
    displayName: model.displayName,
    revision: model.revision,
    package: model.package,
  };
  // The runtime package is WebAssembly rather than weights, so there is nothing to choose.
  if (model.weights.length === 0) {
    return { ...base, dtype: '', assets: model.assets, totalBytes: shared };
  }
  const runnable = model.weights.filter((weights) => halfPrecision || !needsGpu(weights.dtype));
  const chosen = smallest(runnable);
  if (!chosen) {
    throw new Error(`${model.displayName} publishes no weight format this device can run.`);
  }
  return {
    ...base,
    dtype: chosen.dtype,
    assets: [...model.assets, ...chosen.assets],
    totalBytes: shared + chosen.assets.reduce((total, asset) => total + asset.bytes, 0),
  };
}

/** Every format the model publishes, runnable here or not — what the settings screen prices. */
export function publishedDtypes(model: VoiceModel): readonly ModelDtype[] {
  return model.weights.map((weights) => weights.dtype);
}

function smallest(weights: readonly VoiceModelWeights[]): VoiceModelWeights | undefined {
  return weights.reduce<VoiceModelWeights | undefined>(
    (best, candidate) => best === undefined || candidate.totalBytes < best.totalBytes ? candidate : best,
    undefined,
  );
}
